// client/src/context/AuthContext.tsx (TypeScript since ST-00216)
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { AxiosResponse } from 'axios';
import { isAuthEnabled } from '../config/authConfig';
import api from '../services/api';
import { clearLastVisitedRoute } from '../hooks/useLastVisitedRoute';
import { clearTabSession } from '../utils/tabSession';
import { setStorageUser } from '../utils/userStorage';
import {
  setOnAuthLost,
  setOnAccessBlocked,
  refreshSession,
  accessBlockedCode,
  isServerUnreachable,
  isForeignResponse,
  foreignResponseError,
} from '../services/authClient';
import { CONNECTION_PROBLEM_MESSAGE } from '../components/auth/connectionProblemText';
import type {
  AuthContextValue,
  BrandingResponse,
  CurrentTenant,
  CurrentTenantResponse,
  HttpErrorLike,
  LoginResult,
  MyPermissionsResponse,
  SessionResponse,
  SessionUser,
  TenantLogo,
} from '../types/clientShell';

export type { AuthContextValue, CurrentTenant, LoginResult, SessionUser, TenantLogo };

// Set by logout() when the server could not be told (network down), so later
// page loads in this tab (the /login redirect, Back, a typed URL, a reload) do
// not quietly sign the user back in with the cookies the server never got to
// revoke. Kept until the next successful login in this tab (review N21).
const SIGNED_OUT_KEY = 'lb_signed_out';
function markSignedOut(): void {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, '1');
  } catch (_) {
    /* storage unavailable: nothing to remember */
  }
}
function isSignedOutHere(): boolean {
  try {
    return sessionStorage.getItem(SIGNED_OUT_KEY) === '1';
  } catch (_) {
    return false;
  }
}
function clearSignedOut(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** GET /auth/me; a web filter's page answering in its place (HTTP 200, HTML) counts as no answer. */
async function askWhoIsSignedIn(): Promise<AxiosResponse<SessionResponse>> {
  const res: AxiosResponse<SessionResponse> = await api.auth.me();
  if (isForeignResponse(res)) throw foreignResponseError(res);
  return res;
}

/**
 * GET /auth/me for app start-up. The access cookie lives 5 minutes, the refresh
 * cookie 7 days. /auth/me is excluded from the axios 401 -> refresh -> retry
 * interceptor (it is an auth-bootstrap URL), so after more than 5 minutes away
 * every page load, new tab and PWA launch used to land on the login screen with
 * a perfectly good refresh session (audit IAM-6 / PLT-6, ST-00140). On a 401,
 * trade the refresh cookie for a new access cookie once and ask again. No loop:
 * one refresh, one retry; if either fails the user is simply signed out —
 * unless the refresh never reached LexiBridge (ST-00303): then that error is
 * thrown, so the caller can tell "can't reach LexiBridge" from "signed out".
 */
export async function loadSessionUser(): Promise<AxiosResponse<SessionResponse>> {
  try {
    return await askWhoIsSignedIn();
  } catch (e) {
    if ((e as HttpErrorLike | null | undefined)?.response?.status !== 401) throw e;
    try {
      await refreshSession(); // shared single-flight with the axios interceptor
    } catch (refreshError) {
      // A refresh refused for a reason the user should see (organisation
      // suspended / expired) is reported as that, and one that could not get
      // through as that; otherwise simply signed out.
      throw accessBlockedCode(refreshError) || isServerUnreachable(refreshError) ? refreshError : e;
    }
    return askWhoIsSignedIn();
  }
}

/** What a session check found: signed in, not signed in (or refused), or no answer. */
export type SessionOutcome = 'in' | 'out' | 'unreachable';

// While LexiBridge cannot be reached the session check is repeated: 5 s, 10 s,
// 20 s, 40 s, then every 60 s, and at once when the browser comes back online
// (ST-00303). Gentle enough for a whole site behind one web filter.
export const CONNECTION_RETRY_BASE_MS = 5_000;
export const CONNECTION_RETRY_MAX_MS = 60_000;

// Sign-in screen text for a refusal code carried in /login?reason=... (the
// server's own message is used whenever the response that carried it is at hand).
// Looked up by any code string; only the organisation refusals have a text.
export const ACCESS_NOTICES: Readonly<Record<string, string>> = {
  tenant_suspended:
    "Your organisation's access to LexiBridge has been suspended. Please contact your administrator or Lex-Adapt support.",
  tenant_expired:
    "Your organisation's LexiBridge subscription has expired. Please contact your administrator or Lex-Adapt support.",
};

const CHANGE_PASSWORD_PATH = '/set-password?first=1';

// Once-per-tab guard for the organisation redirect (ST-00215 review 2, N2). The
// server checks the organisation in two places (/auth/me and every request); if
// they ever disagree (a transient lookup error), /me would sign the user in and
// the next call would send them back to sign-in, over and over. A second
// organisation refusal within this window shows the sign-in screen in place
// instead of reloading.
const ORG_REDIRECT_KEY = 'lb_org_redirect_at';
const ORG_REDIRECT_WINDOW_MS = 60_000;
function recentOrgRedirect(): boolean {
  try {
    const at = parseInt(sessionStorage.getItem(ORG_REDIRECT_KEY) || '0', 10);
    return at > 0 && Date.now() - at < ORG_REDIRECT_WINDOW_MS;
  } catch (_) {
    return false;
  }
}
function markOrgRedirect(): void {
  try {
    sessionStorage.setItem(ORG_REDIRECT_KEY, String(Date.now()));
  } catch (_) {
    /* storage unavailable: the redirect still happens */
  }
}

/** Message for a blocked-access error (organisation suspended/expired), else null. */
function noticeFor(error: unknown): string | null {
  const code = accessBlockedCode(error);
  if (!code || !ACCESS_NOTICES[code]) return null;
  return (
    (error as HttpErrorLike | null | undefined)?.response?.data?.message || ACCESS_NOTICES[code]!
  );
}

/**
 * Authentication Context (self-hosted cookie sessions).
 *
 * Auth state is derived from GET /api/auth/me — there is no client-held token
 * (the access/refresh tokens live in httpOnly cookies). The DB-driven
 * authorization surface (tenant, permissions, app-roles) is UNCHANGED.
 */

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children?: ReactNode }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenant, setTenant] = useState<CurrentTenant | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [appRoles, setAppRoles] = useState<string[]>([]);
  // True once tenant + permissions for the current session have been loaded
  // (or definitively failed). ProtectedRoute must not evaluate hasPermission()
  // before this, or a gated page 403s against an empty permission list (ST-00147).
  const [contextReady, setContextReady] = useState(false);
  // Tenant brand logo (ST-00127): { dataUri, name } | null. Purely cosmetic —
  // null means "show LexiBridge default branding".
  const [tenantLogo, setTenantLogo] = useState<TenantLogo | null>(null);
  // Why the user is on the sign-in screen when it is not their doing (their
  // organisation was suspended or has expired). Login shows it. ST-00215 IAM-11.
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  // The session check could not reach LexiBridge (ST-00303): the auth screens say
  // so instead of offering the sign-in form, and the check is retried.
  const [connectionProblem, setConnectionProblem] = useState(false);

  const initializationAttempted = useRef(false);
  // The session check in flight, so a click on "Try again", the retry timer and
  // the browser's online event never send two at once.
  const sessionCheck = useRef<Promise<SessionOutcome> | null>(null);

  /**
   * Load the tenant's brand logo. Never throws and never blocks auth: any
   * failure (no logo, 403, network) resolves to null and the app falls back to
   * LexiBridge branding.
   */
  const refreshTenantBranding = useCallback(async (): Promise<TenantLogo | null> => {
    try {
      const res: AxiosResponse<BrandingResponse | null> = await api.logos.getBranding();
      const branding = res?.data;
      if (branding && branding.logoId && branding.dataUri) {
        const next: TenantLogo = { dataUri: branding.dataUri, name: branding.name || null };
        setTenantLogo(next);
        return next;
      }
      setTenantLogo(null);
      return null;
    } catch (e) {
      setTenantLogo(null);
      return null;
    }
  }, []);

  // Load DB-driven tenant + permissions (provider-agnostic; unchanged behaviour).
  const loadUserContext = useCallback(async () => {
    // /tenants/current carries the caller's roles AND permissions (ST-00183), so
    // the separate /users/me/permissions hop only runs when that call fails or
    // an older server omits them — one fewer serial request before the splash clears.
    let permissionsLoaded = false;
    try {
      const tenantRes: AxiosResponse<CurrentTenantResponse> = await api.tenants.getCurrent();
      if (tenantRes.data?.success && tenantRes.data?.data) {
        const t = tenantRes.data.data;
        setTenant(t);
        if (t.currentUser && Array.isArray(t.currentUser.appRoles)) {
          setAppRoles(t.currentUser.appRoles);
        }
        if (t.currentUser && Array.isArray(t.currentUser.appPermissions)) {
          setUserPermissions(t.currentUser.appPermissions);
          permissionsLoaded = true;
        }
      }
    } catch (e) {
      console.warn('Could not load tenant details:', (e as Error).message);
      setTenant({ tenant_name: 'Unknown Organization' });
    }
    if (!permissionsLoaded) {
      try {
        const permRes: AxiosResponse<MyPermissionsResponse> = await api.users.getMyPermissions();
        if (permRes.data?.permissions) setUserPermissions(permRes.data.permissions);
      } catch (e) {
        console.warn('Could not load permissions:', (e as Error).message);
        setUserPermissions([]);
      }
    }
    setContextReady(true);
    // Branding is cosmetic — fire and forget so it can never delay or fail login.
    refreshTenantBranding();
  }, [refreshTenantBranding]);

  /**
   * Ask the server who is signed in and set the auth state from the answer.
   * Never throws. When LexiBridge cannot be reached nothing is decided: the user
   * is not shown as signed out, connectionProblem is set instead (ST-00303).
   */
  const restoreSession = useCallback(async (): Promise<SessionOutcome> => {
    try {
      // Just signed out but the server never heard (see logout): stay out.
      const me = isSignedOutHere() ? null : await loadSessionUser();
      if (me?.data?.user) {
        // Before the shell renders: the tab set and "resume" are this user's (PLT-20).
        setStorageUser(me.data.user.userId);
        setUser(me.data.user);
        if (me.data.user.mustChangePassword) {
          // Every API but the password change answers 403 until it is done
          // (IAM-14): do not load tenant + permissions; the shell sends the
          // user to the change-password screen.
          setContextReady(true);
        } else {
          // Load tenant + permissions first, then flip authenticated, so no consumer
          // ever sees authenticated=true with an empty permission list (ST-00147).
          await loadUserContext();
        }
        setConnectionProblem(false);
        setAuthenticated(true);
        return 'in';
      }
      setStorageUser(null);
      setConnectionProblem(false);
      setAuthenticated(false);
      return 'out';
    } catch (e) {
      if (isServerUnreachable(e)) {
        // No answer from LexiBridge (network, a company web filter, a deploy):
        // the session may be fine. Say so, and keep asking.
        setConnectionProblem(true);
        return 'unreachable';
      }
      // 401 => simply not signed in; the login screen will render. A refusal
      // for the organisation's status says so there.
      setStorageUser(null);
      setAuthNotice(noticeFor(e));
      setConnectionProblem(false);
      setAuthenticated(false);
      return 'out';
    }
  }, [loadUserContext]);

  /** restoreSession, single-flight. */
  const checkSession = useCallback((): Promise<SessionOutcome> => {
    if (!sessionCheck.current) {
      sessionCheck.current = restoreSession().finally(() => {
        sessionCheck.current = null;
      });
    }
    return sessionCheck.current;
  }, [restoreSession]);

  useEffect(() => {
    if (initializationAttempted.current) return;
    initializationAttempted.current = true;

    const init = async () => {
      // Loader-operator PWA: frontend-only bypass (backend routes stay protected).
      if (window.location.pathname.startsWith('/driver')) {
        setAuthenticated(false);
        setTenant({ tenant_name: 'Loader PWA' });
        setUserPermissions(['*']);
        setContextReady(true);
        setInitialized(true);
        setLoading(false);
        return;
      }

      // Dev bypass.
      if (!isAuthEnabled()) {
        setAuthenticated(false);
        setTenant({ tenant_name: 'Default Organization' });
        setUserPermissions(['*']);
        setContextReady(true);
        setInitialized(true);
        setLoading(false);
        try {
          const r: AxiosResponse<CurrentTenantResponse> = await api.tenants.getCurrent();
          if (r.data?.success && r.data?.data) setTenant(r.data.data);
        } catch (_) {
          /* ignore */
        }
        refreshTenantBranding();
        return;
      }

      // When a refresh ultimately fails, drop to the login screen (saying why
      // when the server refused it for the organisation's status).
      setOnAuthLost((refreshError) => {
        const code = accessBlockedCode(refreshError);
        if (
          code &&
          ACCESS_NOTICES[code] &&
          !window.location.pathname.startsWith('/login') &&
          !recentOrgRedirect()
        ) {
          markOrgRedirect();
          // Navigate before touching state: re-rendering signed-out first would
          // route to a bare /login and could replace this navigation (losing the
          // reason). The full page load resets the state anyway.
          window.location.assign(`/login?reason=${code}`);
          return;
        }
        setConnectionProblem(false);
        setAuthenticated(false);
        setStorageUser(null);
        setUser(null);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
      });

      // A valid session the server will not serve right now (ST-00215):
      //  - password_change_required: an admin set a temporary password; every API
      //    answers 403 until it is changed, so go to the change-password screen;
      //  - tenant_suspended / tenant_expired: back to sign-in with the reason.
      setOnAccessBlocked((code, message) => {
        if (code === 'password_change_required') {
          if (!window.location.pathname.startsWith('/set-password')) {
            window.location.assign(CHANGE_PASSWORD_PATH);
          }
          return;
        }
        if (!window.location.pathname.startsWith('/login') && !recentOrgRedirect()) {
          markOrgRedirect();
          window.location.assign(`/login?reason=${code}`); // state untouched, as above
          return;
        }
        // Already sent to sign-in once just now (or already there): show it in
        // place, with the reason, rather than reloading again.
        setAuthNotice(message || ACCESS_NOTICES[code] || null);
        setAuthenticated(false);
        setStorageUser(null);
        setUser(null);
      });

      try {
        await checkSession();
      } finally {
        setInitialized(true);
        setLoading(false);
      }
    };

    init();
  }, [checkSession, refreshTenantBranding]);

  // "Try again" on the Can't reach LexiBridge card.
  const retryConnection = useCallback(async (): Promise<void> => {
    await checkSession();
  }, [checkSession]);

  // While LexiBridge cannot be reached, ask again by itself: on a timer that backs
  // off, and at once when the browser says it is back online. Stops as soon as
  // the server answers (signed in, or really signed out).
  useEffect(() => {
    if (!connectionProblem) return undefined;
    let stopped = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const schedule = () => {
      if (stopped) return;
      clearTimer();
      const delay = Math.min(CONNECTION_RETRY_BASE_MS * 2 ** attempt, CONNECTION_RETRY_MAX_MS);
      attempt += 1;
      timer = setTimeout(tryNow, delay);
    };
    function tryNow() {
      clearTimer();
      void checkSession().then((outcome) => {
        if (outcome === 'unreachable') schedule();
      });
    }
    window.addEventListener('online', tryNow);
    schedule();
    return () => {
      stopped = true;
      clearTimer();
      window.removeEventListener('online', tryNow);
    };
  }, [connectionProblem, checkSession]);

  // Email + password login.
  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setError(null);
      setAuthNotice(null);
      try {
        const res: AxiosResponse<SessionResponse> = await api.auth.login(email, password);
        // A web filter's page in the API's place: the password was never checked.
        if (isForeignResponse(res)) throw foreignResponseError(res);
        clearSignedOut();
        const u = res.data?.user;
        setStorageUser(u?.userId);
        setUser(u);
        if (u?.mustChangePassword) {
          // IAM-14: nothing but the password change is served yet.
          setContextReady(true);
        } else {
          // ST-00147: the tabbed shell mounts the moment authenticated flips and
          // renders the saved (permission-gated) tabs. Load the context first so
          // those tabs never evaluate hasPermission() against [] and 403.
          await loadUserContext();
        }
        setAuthenticated(true);
        return { success: true, user: u };
      } catch (e) {
        const err = e as HttpErrorLike;
        if (isServerUnreachable(e)) {
          // ST-00303: no answer from LexiBridge is not a wrong password.
          setError(CONNECTION_PROBLEM_MESSAGE);
          return {
            success: false,
            error: CONNECTION_PROBLEM_MESSAGE,
            status: err.response?.status,
            unreachable: true,
          };
        }
        const msg = err.response?.data?.message || 'Login failed. Please try again.';
        setError(msg);
        return { success: false, error: msg, status: err.response?.status };
      }
    },
    [loadUserContext]
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch (_) {
      // The server did not revoke the refresh session: make sure the reload
      // below does not use it to sign straight back in.
      markSignedOut();
    }
    // A shared PC: the next person must not get this user's tabs or be offered their last
    // page (PLT-20, ST-00230).
    clearTabSession();
    clearLastVisitedRoute();
    setStorageUser(null);
    setAuthenticated(false);
    setUser(null);
    setTenant(null);
    setUserPermissions([]);
    setAppRoles([]);
    setContextReady(false);
    setTenantLogo(null);
    window.location.assign('/login');
  }, []);

  // ---- authorization helpers (DB-driven; unchanged semantics) ----
  const hasRole = useCallback(
    (role: string) => {
      if (!isAuthEnabled()) return true;
      return appRoles.includes(role);
    },
    [appRoles]
  );

  const hasAnyRole = useCallback(
    (roles: readonly string[] | null | undefined) => {
      if (!isAuthEnabled()) return true;
      return Array.isArray(roles) && roles.some((r) => appRoles.includes(r));
    },
    [appRoles]
  );

  const hasPermission = useCallback(
    (permissionKey: string) => {
      if (!isAuthEnabled()) return true;
      if (userPermissions.includes('*')) return true;
      return userPermissions.includes(permissionKey);
    },
    [userPermissions]
  );

  const hasAppRole = useCallback(
    (role: string) => {
      if (!isAuthEnabled()) return true;
      return appRoles.includes(role);
    },
    [appRoles]
  );

  const hasAnyAppRole = useCallback(
    (roles: readonly string[] | null | undefined) => {
      if (!isAuthEnabled()) return true;
      if (!Array.isArray(roles)) return false;
      return roles.some((r) => appRoles.includes(r));
    },
    [appRoles]
  );

  const value: AuthContextValue = {
    authenticated,
    initialized,
    loading,
    error,
    authNotice,
    connectionProblem,
    retryConnection,
    // True while the server refuses everything but the password change (IAM-14).
    mustChangePassword: !!(authenticated && user && user.mustChangePassword),
    user,
    tenant,
    // ST-00127 tenant branding contract: { dataUri, name } | null
    tenantLogo,
    refreshTenantBranding,
    userPermissions,
    appRoles,
    contextReady,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAppRole,
    hasAnyAppRole,
    authEnabled: isAuthEnabled(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
